import React, { useCallback, useEffect, useImperativeHandle, useState } from 'react'
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { ICaptchaProviderProps, ICaptchaProviderRef } from '../types'

// Internal reCAPTCHA component that uses the hook
const RecaptchaV3Component = React.forwardRef<ICaptchaProviderRef, ICaptchaProviderProps>(
  ({ onVerify, onError }, ref) => {
    const { executeRecaptcha } = useGoogleReCaptcha()
    const [isReady, setIsReady] = useState(false)

    // Track component readiness
    useEffect(() => {
      if (executeRecaptcha) {
        setIsReady(true)
      }
    }, [executeRecaptcha])

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
      execute: async (action?: string): Promise<string> => {
        if (!executeRecaptcha) {
          throw new Error('reCAPTCHA is not ready')
        }

        try {
          const token = await executeRecaptcha(action || 'form_submit')
          onVerify?.(token)
          return token
        } catch (error: any) {
          const errorMessage = error.message || 'reCAPTCHA execution failed'
          onError?.(new Error(errorMessage))
          throw error
        }
      },

      reset: () => {
        // reCAPTCHA v3 doesn't need explicit reset
        // The token is single-use by design
      },

      ready: isReady
    }), [executeRecaptcha, onVerify, onError, isReady])

    // This component doesn't render anything visible (invisible reCAPTCHA)
    return null
  }
)

RecaptchaV3Component.displayName = 'RecaptchaV3Component'

// Main provider component that wraps with GoogleReCaptchaProvider
export const RecaptchaV3Provider = React.forwardRef<ICaptchaProviderRef, ICaptchaProviderProps>(
  (props, ref) => {
    const { siteKey } = props

    // Validate siteKey
    if (!siteKey) {
      console.error('reCAPTCHA siteKey is required')
      return null
    }

    return (
      <GoogleReCaptchaProvider
        reCaptchaKey={siteKey}
        scriptProps={{
          async: true,
          defer: true,
          appendTo: 'head',
          nonce: undefined,
        }}
        useRecaptchaNet={false}
        useEnterprise={false}
      >
        <RecaptchaV3Component ref={ref} {...props} />
      </GoogleReCaptchaProvider>
    )
  }
)

RecaptchaV3Provider.displayName = 'RecaptchaV3Provider'

// Hook for using reCAPTCHA v3 (alternative to component)
export const useRecaptchaV3 = (action?: string) => {
  const { executeRecaptcha } = useGoogleReCaptcha()
  const [isReady, setIsReady] = useState(false)

  useEffect(() => {
    if (executeRecaptcha) {
      setIsReady(true)
    }
  }, [executeRecaptcha])

  const execute = useCallback(async (customAction?: string): Promise<string> => {
    if (!executeRecaptcha) {
      throw new Error('reCAPTCHA is not ready')
    }

    try {
      const token = await executeRecaptcha(customAction || action || 'form_submit')
      return token
    } catch (error: any) {
      throw new Error(`reCAPTCHA execution failed: ${error.message}`)
    }
  }, [executeRecaptcha, action])

  return {
    execute,
    isReady
  }
}
